import React, { useEffect, useRef } from "react";

import { GenericErrorSet, IFieldSet, IValueSet } from "../../models/formFields";
import { FormStep, FormStepType } from "../../models/forms";
import {
    focusElement,
    focusWithTemporaryTabIndex,
    getFocusableElems,
} from "../../utils/keyboardFocus";
import { AutoFields } from "../AutoFields";
import { FieldsSummary } from "../FieldsSummary";

interface BaseProps<T extends string> {
    className: string;

    fields: IFieldSet<T>;

    onChange: (name: T, value: string) => void;
    onValidStateChange: (fieldName: T, errorMessages: string[]) => void;
    onSummaryEdit: (step: number) => void;

    values: IValueSet<T>;

    disabled: boolean;

    showErrorMessages: boolean;
    errors: GenericErrorSet<T>;
}

const focusStepStart = (container: HTMLElement | null): void => {
    if (!container) {
        return;
    }

    // Neither call forces `focusVisible`: the ring is left to the browser's
    // modality heuristic, which carries :focus-visible over from a
    // keyboard-activated control to this scripted focus move. Forcing it would
    // outline the heading for mouse users, who did not ask to see focus.
    const heading = container.querySelector("h2, h3");
    if (heading instanceof HTMLElement) {
        focusWithTemporaryTabIndex(heading);
        return;
    }

    const firstFocusable = getFocusableElems(container)[0];
    if (firstFocusable) {
        focusElement(firstFocusable);
    }
};

interface SingleStepProps<FieldNames extends string> {
    step: FormStep<FieldNames>;
    index: number;
}

const StepForm = <T extends string>(
    props: BaseProps<T> & SingleStepProps<T>,
): React.JSX.Element | null => {
    switch (props.step.stepType) {
        case FormStepType.FieldSet:
            return (
                <AutoFields
                    heading={props.step.groupTitle}
                    fieldOrder={props.step.fields}
                    fields={props.fields}
                    values={props.values}
                    errors={props.errors}
                    disabled={props.disabled}
                    onChange={props.onChange}
                    onValidStateChange={props.onValidStateChange}
                    showErrorMessages={props.showErrorMessages}
                />
            );

        case FormStepType.OtherJSX:
            return props.step.buildContent(props.index);
    }
};

const StepSummary = <T extends string>(
    props: BaseProps<T> & SingleStepProps<T>,
): React.JSX.Element | null => {
    switch (props.step.stepType) {
        case FormStepType.FieldSet:
            return (
                <FieldsSummary
                    key={props.index}
                    step={props.index}
                    heading={props.step.groupTitle}
                    fieldOrder={props.step.fields}
                    fields={props.fields}
                    values={props.values}
                    onSummaryEdit={props.onSummaryEdit}
                />
            );

        case FormStepType.OtherJSX:
            return props.step.summary ? props.step.summary : null;
    }
};

export const AutoFieldsMultistep = <T extends string>(
    props: BaseProps<T> & {
        steps: Readonly<FormStep<T>[]>;
        currStepIndex: number;
        disabledSteps: readonly number[];
    },
): React.JSX.Element | null => {
    const activeStepRef = useRef<HTMLDivElement>(null);
    const pendingSummaryEditFocusRef = useRef(-1);

    const currentStep = props.steps[props.currStepIndex];
    const pastSteps = props.steps.slice(0, props.currStepIndex);

    const onSummaryEdit = (step: number): void => {
        pendingSummaryEditFocusRef.current = step;
        props.onSummaryEdit(step);
    };

    useEffect(() => {
        if (pendingSummaryEditFocusRef.current !== props.currStepIndex) {
            return;
        }
        pendingSummaryEditFocusRef.current = -1;
        requestAnimationFrame(() => {
            focusStepStart(activeStepRef.current);
        });
    }, [props.currStepIndex]);

    if (!currentStep) {
        return null;
    }

    return (
        <div className={props.className}>
            {pastSteps.map((step, i) => (
                <StepSummary
                    key={i}
                    {...props}
                    onSummaryEdit={onSummaryEdit}
                    step={step}
                    index={i}
                />
            ))}
            <div ref={activeStepRef}>
                <StepForm
                    {...props}
                    step={currentStep}
                    index={props.currStepIndex}
                    disabled={
                        props.disabled ||
                        props.disabledSteps.includes(props.currStepIndex)
                    }
                />
            </div>
        </div>
    );
};
